import React from 'react';
import {
  Box,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import moment from 'moment';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import ReactResizeDetector from 'react-resize-detector';
import { v4 } from 'uuid';
import clsx from 'clsx';
import {
  Column,
  Table,
  TableHeaderRowProps,
  TableRowProps,
} from 'react-virtualized';

import styles from './ResultTable.styles';
import {
  RequestInfo,
  RequestListResult,
  RequestOrderInfo,
} from '../types';
import ProgressMask from '../../../../components/progressMask';
import NetworkState from '../../../../core/types/network';
import getPersonDisplayName from '../../person/utils';

const useStyles = makeStyles(styles);

type Props = {
  requestLists?: RequestListResult;
  resultNetworkStates: {
    resultRequest: NetworkState;
  };
};

const ResultTable: React.FC<Props> = ({
  requestLists,
  resultNetworkStates: {
    resultRequest,
  },
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  const isMediumScreen = useMediaQuery((themeS: Theme) => themeS.breakpoints.up('sm'));

  const TestOrderCompact: React.FC<{ order: RequestOrderInfo[] }> = ({
    order,
  }: { order: RequestOrderInfo[] }) => (
    <Box className={classes.statusBox}>
      {order.map((list, index) => (
        <Typography
          key={v4()}
          className={clsx({
            [classes.orderCancelled]: list.status === 'cancelled',
            [classes.orderProcess]: list.status === 'process',
            [classes.orderNormal]: list.status === 'complete' && !list.flag,
            [classes.orderAbnormal]: list.status === 'complete' && list.flag,
          })}
          component="span"
          color="inherit"
          variant="body2"
          align="center"
        >
          {index === 0 ? list.name : `, ${list.name}`}
        </Typography>
      ))}
    </Box>
  );

  const headerRowRenderer = ({
    columns,
    style,
  }: TableHeaderRowProps) => (
    <Box
      className={classes.tableHeaderRow}
      role="row"
      style={style}
    >
      {columns}
    </Box>
  );

  const rowRenderer = ({
    key,
    columns,
    style,
    index,
    rowData,
  }: TableRowProps) => (
    <Box
      key={key}
      className={
        classNames(
          index % 2 === 0 ? classes.tableEvenRow : classes.tableOddRow,
          classes.tableHover,
          (rowData as RequestInfo).status === 'cancelled' ? classes.orderCancelled : '',
        )
      }
      role="row"
      style={style}
      onClick={() => {
        const url = [
          `id=${(rowData as RequestInfo).id}`,
        ];
        window.open(`resultDetails?${url.join('&')}`);
      }}
    >
      <div className={classes.tableRow}>{columns}</div>
      {!isMediumScreen && (
        <div className={classes.tableRowSub}>
          <Tooltip
            title={(rowData as RequestInfo).testOrder?.map((val) => val.name).join(', ') || ''}
          >
            <Box>
              {TestOrderCompact({ order: (rowData as RequestInfo).testOrder || [] })}
            </Box>
          </Tooltip>
        </div>
      )}
    </Box>
  );

  return (
    <Box className={classes.root}>
      {resultRequest.isRequesting && (
        <ProgressMask />
      )}
      {!resultRequest.isRequesting && requestLists && (
        <ReactResizeDetector handleHeight>
          {({ width, height }) => (
            <Table
              height={height || 0}
              width={width || 0}
              rowCount={requestLists.requestLists.length}
              rowGetter={({ index }) => requestLists.requestLists[index]}
              rowHeight={isMediumScreen ? theme.spacing(4) : theme.spacing(7)}
              headerHeight={theme.spacing(4)}
              gridClassName={classes.tableGrid}
              headerRowRenderer={headerRowRenderer}
              headerClassName={classes.tableHeaderCell}
              rowRenderer={rowRenderer}
              className={classes.table}
            >
              <Column
                label="Date"
                cellDataGetter={({ rowData }) => ((rowData as RequestInfo).date
                  ? moment((rowData as RequestInfo).date).format('DD/MM/YYYY')
                  : '')}
                dataKey="date"
                width={100}
                className={classNames(classes.tableCell)}
              />
              <Column
                label="Lab Number"
                className={classNames(classes.tableCell)}
                cellDataGetter={({ rowData }) => (rowData as RequestInfo).labNumber}
                dataKey="labNumber"
                width={100}
              />
              <Column
                label="Patient Name"
                cellDataGetter={({ rowData }) => getPersonDisplayName((rowData as RequestInfo).patient?.person) || ''}
                dataKey="patient"
                width={200}
                className={classNames(classes.tableCell)}
              />
              <Column
                label="Age (Y)"
                cellDataGetter={({ rowData }) => (rowData as RequestInfo).age?.year}
                dataKey="age.year"
                width={65}
                className={classNames(classes.tableCell, classes.tableCellCenter)}
              />
              {isMediumScreen && (
                <Column
                  label="Req.Number"
                  cellDataGetter={({ rowData }) => (rowData as RequestInfo).reqNumber}
                  dataKey="reqNumber"
                  width={100}
                  className={classNames(classes.tableCell)}
                />
              )}
              {isMediumScreen && (
                <Column
                  label="Status"
                  cellDataGetter={({ rowData }) => (rowData as RequestInfo).testOrder}
                  dataKey="status"
                  width={350}
                  className={classNames(classes.tableCell)}
                  cellRenderer={({ rowData }) => (
                    <Tooltip
                      title={(rowData as RequestInfo).testOrder?.map((val) => val.name).join(', ') || ''}
                    >
                      <Box>
                        {TestOrderCompact({ order: (rowData as RequestInfo).testOrder || [] })}
                      </Box>
                    </Tooltip>
                  )}
                />
              )}
            </Table>
          )}
        </ReactResizeDetector>
      )}
    </Box>
  );
};

export default ResultTable;
